/**
 * SDDEditor Component
 *
 * Edit SDD document with sections, status management, and auto-save
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Save, X, Plus, Trash2, AlertCircle, Loader2 } from 'lucide-react';
import { useSDD, useUpdateSDD, type UpdateSDDRequest } from '@/hooks/useSDD';
import { useToast } from '@/shared/hooks/useToast';
import type { SDDDocumentStatus } from '@forgenetwork/types';

interface SDDEditorProps {
  repoId: string;
  sddId: string;
  onSave?: () => void;
  onCancel?: () => void;
}

interface ParsedSection {
  id: string;
  title: string;
  content: string;
  level: number;
}

interface EditedSDD {
  title: string;
  status: SDDDocumentStatus;
  sections: ParsedSection[];
}

/**
 * Parse SDD content into sections for editing
 */
function parseSDDContent(content: string): ParsedSection[] {
  try {
    const parsed = JSON.parse(content);
    if (parsed.sections && Array.isArray(parsed.sections)) {
      return parsed.sections;
    }
    return [{
      id: 'content',
      title: 'Content',
      content: typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2),
      level: 1,
    }];
  } catch {
    // Parse markdown headings into sections
    const lines = content.split('\n');
    const sections: ParsedSection[] = [];
    let currentSection: ParsedSection | null = null;
    let contentLines: string[] = [];

    for (const line of lines) {
      const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
      if (headingMatch && headingMatch[1] && headingMatch[2]) {
        if (currentSection) {
          currentSection.content = contentLines.join('\n').trim();
          sections.push(currentSection);
        }
        currentSection = {
          id: `section-${sections.length}`,
          title: headingMatch[2],
          content: '',
          level: headingMatch[1].length,
        };
        contentLines = [];
      } else if (currentSection) {
        contentLines.push(line);
      } else {
        if (line.trim()) {
          currentSection = {
            id: 'intro',
            title: 'Overview',
            content: '',
            level: 1,
          };
          contentLines.push(line);
        }
      }
    }

    if (currentSection) {
      currentSection.content = contentLines.join('\n').trim();
      sections.push(currentSection);
    }

    if (sections.length === 0) {
      return [{
        id: 'content',
        title: 'Content',
        content: content,
        level: 1,
      }];
    }

    return sections;
  }
}

/**
 * Serialize sections back to content format
 */
function serializeSections(sections: ParsedSection[]): string {
  return JSON.stringify({ sections }, null, 2);
}

/**
 * SDDEditor - Edit SDD document with auto-save
 */
export function SDDEditor({ repoId, sddId, onSave, onCancel }: SDDEditorProps): React.JSX.Element {
  const { data: sdd, isLoading, error } = useSDD(repoId, sddId);
  const updateSDD = useUpdateSDD();
  const { toast } = useToast();

  const [editedSDD, setEditedSDD] = React.useState<EditedSDD | null>(null);
  const [isDirty, setIsDirty] = React.useState(false);

  // Initialize edited SDD when data loads
  React.useEffect(() => {
    if (sdd && !editedSDD) {
      setEditedSDD({
        title: sdd.title,
        status: sdd.status,
        sections: parseSDDContent(sdd.content),
      });
    }
  }, [sdd, editedSDD]);

  const handleFieldChange = <K extends keyof EditedSDD>(field: K, value: EditedSDD[K]): void => {
    setEditedSDD((prev) => (prev ? { ...prev, [field]: value } : null));
    setIsDirty(true);
  };

  const handleSave = React.useCallback((): void => {
    if (!editedSDD) return;

    const updates: UpdateSDDRequest = {
      title: editedSDD.title,
      status: editedSDD.status,
      content: serializeSections(editedSDD.sections),
    };

    updateSDD.mutate(
      { repoId, sddId, updates },
      {
        onSuccess: () => {
          setIsDirty(false);
          toast({
            title: 'SDD Saved',
            description: 'Your changes have been saved successfully.',
          });
          onSave?.();
        },
        onError: (err: Error) => {
          toast({
            title: 'Save Failed',
            description: err.message || 'Failed to save changes',
            variant: 'destructive',
          });
        },
      }
    );
  }, [editedSDD, repoId, sddId, updateSDD, toast, onSave]);

  // Auto-save with debounce
  const autoSaveTimeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);
  React.useEffect(() => {
    if (isDirty && editedSDD) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        handleSave();
      }, 3000); // Auto-save after 3 seconds of inactivity
    }
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [isDirty, editedSDD, handleSave]);

  const handleCancel = (): void => {
    if (sdd) {
      setEditedSDD({
        title: sdd.title,
        status: sdd.status,
        sections: parseSDDContent(sdd.content),
      });
      setIsDirty(false);
    }
    onCancel?.();
  };

  // Section management
  const handleAddSection = (): void => {
    if (!editedSDD) return;
    const newSection: ParsedSection = {
      id: `section-${Date.now()}`,
      title: 'New Section',
      content: '',
      level: 2,
    };
    handleFieldChange('sections', [...editedSDD.sections, newSection]);
  };

  const handleUpdateSection = (sectionId: string, updates: Partial<ParsedSection>): void => {
    if (!editedSDD) return;
    const updated = editedSDD.sections.map((s) =>
      s.id === sectionId ? { ...s, ...updates } : s
    );
    handleFieldChange('sections', updated);
  };

  const handleRemoveSection = (sectionId: string): void => {
    if (!editedSDD) return;
    handleFieldChange(
      'sections',
      editedSDD.sections.filter((s) => s.id !== sectionId)
    );
  };

  const handleMoveSection = (sectionId: string, direction: 'up' | 'down'): void => {
    if (!editedSDD) return;
    const index = editedSDD.sections.findIndex((s) => s.id === sectionId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= editedSDD.sections.length) return;

    const newSections = [...editedSDD.sections];
    const temp = newSections[index];
    newSections[index] = newSections[newIndex]!;
    newSections[newIndex] = temp!;
    handleFieldChange('sections', newSections);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-muted animate-pulse rounded-xl" />
        <div className="h-64 bg-muted animate-pulse rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <p>{error instanceof Error ? error.message : 'Failed to load SDD'}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!editedSDD) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">SDD not found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Edit SDD</CardTitle>
            <div className="flex items-center gap-2">
              {isDirty && (
                <Badge variant="secondary" className="gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Unsaved Changes
                </Badge>
              )}
              {updateSDD.isPending && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={updateSDD.isPending}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!isDirty || updateSDD.isPending}
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={editedSDD.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder="SDD Title"
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={editedSDD.status}
              onValueChange={(value) => handleFieldChange('status', value as SDDDocumentStatus)}
            >
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_review">Pending Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Sections</CardTitle>
            <Button size="sm" onClick={handleAddSection}>
              <Plus className="h-4 w-4 mr-2" />
              Add Section
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" defaultValue={editedSDD.sections.map((s) => s.id)}>
            {editedSDD.sections.map((section, index) => (
              <AccordionItem key={section.id} value={section.id}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2 flex-1">
                    <span className="font-semibold">{section.title}</span>
                    <Badge variant="outline" className="text-xs">
                      H{section.level}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    {/* Section Title */}
                    <div className="flex items-center gap-2">
                      <Input
                        value={section.title}
                        onChange={(e) =>
                          handleUpdateSection(section.id, { title: e.target.value })
                        }
                        placeholder="Section title"
                        className="flex-1"
                      />
                      <Select
                        value={String(section.level)}
                        onValueChange={(value) =>
                          handleUpdateSection(section.id, { level: parseInt(value, 10) })
                        }
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">H1</SelectItem>
                          <SelectItem value="2">H2</SelectItem>
                          <SelectItem value="3">H3</SelectItem>
                          <SelectItem value="4">H4</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Section Content */}
                    <Textarea
                      value={section.content}
                      onChange={(e) =>
                        handleUpdateSection(section.id, { content: e.target.value })
                      }
                      placeholder="Section content (Markdown supported)"
                      rows={10}
                      className="font-mono text-sm"
                    />

                    {/* Section Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleMoveSection(section.id, 'up')}
                          disabled={index === 0}
                        >
                          Move Up
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleMoveSection(section.id, 'down')}
                          disabled={index === editedSDD.sections.length - 1}
                        >
                          Move Down
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleRemoveSection(section.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {editedSDD.sections.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p>No sections yet. Click "Add Section" to get started.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
